// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file MetadataList.tsx
 * @input Uses React, ReactNode, StyleXStyles, theme tokens, MetadataListContext
 * @output Exports MetadataList component, MetadataListProps, MetadataListColumns types
 * @position Core implementation; consumed by index.ts, tested by MetadataList.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/MetadataList/MetadataList.doc.mjs
 * - /packages/core/src/MetadataList/MetadataList.test.tsx
 * - /packages/core/src/MetadataList/index.ts
 * - /apps/storybook/stories/MetadataList.stories.tsx
 * - /packages/cli/templates/blocks/components/MetadataList/ (showcase blocks)
 */
import { Children, useId, useMemo, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars, colorVars, typeScaleVars, fontWeightVars } from "../theme/tokens.stylex.js";
import { MetadataListContext } from "./MetadataListContext.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Types
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    $$css: true
  },
  dl: {
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    $$css: true
  },
  gridSingle: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x1pmbctz",
    kOIVth: "xpec5dj",
    kGNEyG: "x1pha0wt",
    $$css: true
  },
  gridMulti: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x189bvgu",
    kOIVth: "x18g69wz",
    $$css: true
  },
  gridStackedSingle: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x1y6fwsi",
    kOIVth: "xjcht0a",
    $$css: true
  },
  gridStackedMulti: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x189bvgu",
    kOIVth: "x18g69wz",
    $$css: true
  },
  horizontal: {
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np",
    kwnvtZ: "x1a02dak",
    kOIVth: "x18g69wz",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * A read-only labeled list for displaying key-value metadata.
 *
 * Renders semantic `<dl>` / `<dt>` / `<dd>` elements. Supports multiple
 * column layouts, horizontal orientation, and show more/less collapsing.
 *
 * @example
 * ```
 * <MetadataList columns="multi">
 *   <MetadataListItem label="Name">MetadataList</MetadataListItem>
 *   <MetadataListItem label="Status">Active</MetadataListItem>
 * </MetadataList>
 * ```
 */
const LABEL_START = {
  position: 'start'
};
const LABEL_TOP = {
  position: 'top'
};
export function MetadataList({
  children,
  columns = 'single',
  label,
  maxNumOfItems,
  orientation = 'vertical',
  title,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref
}) {
  const isMultiColumn = columns === 'multi' || typeof columns === 'number' && columns > 1;
  // Default to 'top' for multi-column and horizontal layouts since side labels
  // don't work well when items are in separate grid cells.
  const labelConfig = label ?? (isMultiColumn ? LABEL_TOP : LABEL_START);
  const [isShowAll, setIsShowAll] = useState(false);
  const contentId = useId();
  const contextValue = useMemo(() => ({
    labelConfig: orientation === 'horizontal' ? LABEL_TOP : labelConfig,
    orientation
  }), [labelConfig, orientation]);
  const childrenArray = Children.toArray(children);
  const isHorizontal = orientation === 'horizontal';

  // In horizontal mode, ignore maxNumOfItems
  const effectiveMax = isHorizontal ? undefined : maxNumOfItems;
  const isExceedMax = effectiveMax != null && childrenArray.length > effectiveMax;
  const visibleChildren = isExceedMax && !isShowAll ? childrenArray.slice(0, effectiveMax) : childrenArray;
  const titleContent = title != null ? /*#__PURE__*/_jsx("div", {
    ...{
      className: "xep27e5"
    },
    children: title
  }) : null;

  // Determine grid style based on columns and label position
  const getGridStyle = () => {
    if (isHorizontal) {
      return styles.horizontal;
    }
    const isStacked = labelConfig.position === 'top';
    if (isStacked) {
      return columns === 'single' || columns === 1 ? styles.gridStackedSingle : styles.gridStackedMulti;
    }
    // Labels on the left
    if (columns === 'single' || columns === 1) {
      return styles.gridSingle;
    }
    return styles.gridMulti;
  };

  // For numeric columns > 1 with side labels, use inline style for dynamic grid
  const dynamicGridStyle = !isHorizontal && labelConfig.position === 'start' && typeof columns === 'number' && columns > 1 ? {
    gridTemplateColumns: `repeat(${columns}, auto 1fr)`
  } : !isHorizontal && labelConfig.position === 'start' && labelConfig.width != null ? {
    gridTemplateColumns: `${typeof labelConfig.width === 'number' ? `${labelConfig.width}px` : labelConfig.width} 1fr`
  } : undefined;
  return /*#__PURE__*/_jsx(MetadataListContext, {
    value: contextValue,
    children: /*#__PURE__*/_jsxs("div", {
      ref: ref,
      "data-testid": testId,
      ...mergeProps(themeProps('metadata-list', {
        columns: String(columns),
        orientation
      }), stylex.props(styles.root, xstyle), className, style),
      children: [titleContent, /*#__PURE__*/_jsx("dl", {
        id: contentId,
        ...mergeProps(stylex.props(styles.dl, getGridStyle()), {
          style: dynamicGridStyle
        }),
        children: visibleChildren
      }), isExceedMax && /*#__PURE__*/_jsx("button", {
        type: "button",
        "aria-controls": contentId,
        "aria-expanded": isShowAll,
        onClick: () => setIsShowAll(prev => !prev),
        ...{
          className: "xjyslct x15nmkw0 x1ypdohk xqwr325 xjm74w1 xw6l6zx x1e4wzip xjb2p0i x1yc453h xqcrz7y"
        },
        children: isShowAll ? 'Show less' : 'Show more'
      })]
    })
  });
}
MetadataList.displayName = 'MetadataList';